/**
 * @fileoverview Componente responsável por proteger rotas públicas e privadas com base no estado de autenticação.
 */

import React, { useContext, useEffect } from "react";
import { Route as RouterRoute, Redirect } from "react-router-dom";
import { toast } from "react-toastify";

import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { isInternalOnly, getHomeRoute } from "../utils/accessScope";
import { allows } from "../components/Can";
import usePermission from "../hooks/usePermission";
import { i18n } from "../translate/i18n";

// Página aberta pelo endereço sem a permissão: volta para a tela inicial e avisa.
// Se a própria tela inicial é a negada (acesso sem nenhuma tela), só avisa:
// voltar para ela seria redirecionar em laço.
const DeniedRedirect = ({ to, from }) => {
	useEffect(() => {
		toast.info(i18n.t("backendErrors.ERR_NO_PERMISSION"), { toastId: "route-no-permission" });
	}, []);
	if (from?.pathname === to) return null;
	return <Redirect to={{ pathname: to, state: { from } }} />;
};

/**
 * @param {boolean} isPrivate          exige usuário autenticado
 * @param {boolean} requiresAttendance rota do módulo de atendimento — negada a
 *                                     quem tem escopo "Apenas Interno".
 *                                     NUNCA marcar /internal-chat com isto: é o
 *                                     destino do redirect e viraria loop.
 * @param {string}  permission         regra do rules.js (ex.: "dashboard:view"):
 *                                     sem ela, quem abre o endereço volta para a
 *                                     tela inicial. O menu já esconde e a API recusa.
 * @param {string|function} catalog    a permissão do catálogo da tela (ex.:
 *                                     "reports.view"), ou a função que a escolhe
 *                                     pelo usuário: com a regra nova ligada, vale
 *                                     ela; sem, a regra do rules.js.
 * @param {function} also              o que também libera a rota em qualquer modo
 *                                     (user, { hasDepartments }), como o do item do
 *                                     menu em layout/navigation.js.
 */
const Route = ({
	component: Component,
	isPrivate = false,
	requiresAttendance = false,
	permission = null,
	catalog = null,
	also = null,
	...rest
}) => {
	const { isAuth, loading, user } = useContext(AuthContext);
	const { can, enforce, loaded, hasDepartments } = usePermission();

	if (!isAuth && isPrivate) {
		return (
			<>
				{loading && <BackdropLoading />}
				<Redirect to={{ pathname: "/login", state: { from: rest.location } }} />
			</>
		);
	}

	// Logado numa tela pública (login, cadastro): a raiz escolhe a tela inicial,
	// com o acesso da pessoa já lido.
	if (isAuth && !isPrivate) {
		return (
			<>
				{loading && <BackdropLoading />}
				<Redirect to={{ pathname: "/", state: { from: rest.location } }} />
			</>
		);
	}

	// Com a permissão do catálogo, espera o acesso da pessoa (GET /auth/access):
	// antes dele, a regra de hoje mandaria embora quem a regra nova deixa entrar.
	if (isAuth && isPrivate && catalog && !loaded) {
		return <RouterRoute {...rest} render={() => <BackdropLoading />} />;
	}

	// Escopo "Apenas Interno" não entra no módulo de atendimento nem digitando a
	// URL. Sem isto a trava seria só o item de menu escondido. Vale só quando o
	// endereço é desta rota: as rotas do layout não ficam num Switch, então
	// conferir antes do path mandava toda página (CRM, usuários...) de volta ao
	// chat interno, em laço. Com a regra nova ligada, a licença já está nas
	// permissões de cada rota (o catálogo, abaixo), e o super passa como na API.
	if (isAuth && isPrivate && requiresAttendance && !enforce && isInternalOnly(user)) {
		return (
			<RouterRoute
				{...rest}
				render={(props) => (
					<Redirect to={{ pathname: getHomeRoute(user), state: { from: props.location } }} />
				)}
			/>
		);
	}

	// Sem o perfil carregado ainda (recarregando a página), não decide.
	// Sem regra do rules.js, a rota hoje é de todos (o "permitido antes").
	const permitted = () => {
		if (also && also(user, { hasDepartments })) return true;
		const before = permission ? allows(user, permission) : true;
		if (!catalog) return before;
		return can(typeof catalog === "function" ? catalog(user) : catalog, before);
	};
	if (isAuth && isPrivate && (permission || catalog) && user?.profile && !permitted()) {
		return (
			<RouterRoute
				{...rest}
				render={(props) => <DeniedRedirect to={getHomeRoute(user)} from={props.location} />}
			/>
		);
	}

	return (
		<>
			{loading && <BackdropLoading />}
			<RouterRoute {...rest} component={Component} />
		</>
	);
};

export default Route;
